/**
 * Шлюз к облачному backend (ARCH-TASK-65.12.4).
 *
 * Ядро владеет адресом облака и токеном авторизации, модули — только путями
 * своих эндпоинтов. Так плагинам не нужно знать про `META_AUTH_TOKEN`.
 */
import { db } from '@core/db/db'
import { META_DEVICE_TOKEN } from '@core/db/schema'

export interface CloudResponse {
  status: number
  body: unknown
}

export interface CloudRequestInit {
  method?: string
  body?: unknown
  /** Таймаут запроса, мс (по умолчанию 10 000). */
  timeoutMs?: number
}

export interface CloudGateway {
  readonly baseUrl: string
  /** Запрос к облаку с подстановкой Bearer-токена. */
  request(path: string, init?: CloudRequestInit): Promise<CloudResponse>
}

/** Ошибка шлюза: нет токена или сеть/таймаут. */
export class CloudError extends Error {
  constructor(
    message: string,
    readonly kind: 'not_connected' | 'network' | 'timeout'
  ) {
    super(message)
    this.name = 'CloudError'
  }
}

export interface CloudGatewayOptions {
  baseUrl?: string
  fetchImpl?: typeof fetch
  /** Источник токена (по умолчанию — meta кассы). */
  getToken?: () => Promise<string | null>
}

/** Создать шлюз. Все зависимости можно подменить в тестах. */
export function createCloudGateway(options: CloudGatewayOptions = {}): CloudGateway {
  const resolved = (
    options.baseUrl ??
    import.meta.env?.VITE_API_URL ??
    'https://api.pospoint.ru/api/v1'
  ).replace(/\/+$/, '')
  const doFetch = options.fetchImpl ?? fetch
  const getToken =
    options.getToken ??
    (async () => {
      const entry = await db.meta.get(META_DEVICE_TOKEN)
      return typeof entry?.value === 'string' ? entry.value : null
    })

  return {
    baseUrl: resolved,

    async request(path: string, init?: CloudRequestInit): Promise<CloudResponse> {
      const token = await getToken()
      if (!token) {
        throw new CloudError('Касса не привязана к облаку', 'not_connected')
      }

      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), init?.timeoutMs ?? 10_000)
      try {
        const response = await doFetch(`${resolved}${path}`, {
          method: init?.method ?? 'GET',
          headers: {
            accept: 'application/json',
            authorization: `Bearer ${token}`,
            ...(init?.body !== undefined ? { 'content-type': 'application/json' } : {})
          },
          body: init?.body !== undefined ? JSON.stringify(init.body) : undefined,
          signal: controller.signal
        })

        const text = await response.text()
        let body: unknown = null
        if (text) {
          try {
            body = JSON.parse(text)
          } catch {
            body = text
          }
        }
        return { status: response.status, body }
      } catch (error) {
        if (controller.signal.aborted) {
          throw new CloudError('Таймаут запроса к облаку', 'timeout')
        }
        throw new CloudError(
          error instanceof Error ? error.message : 'Сеть недоступна',
          'network'
        )
      } finally {
        clearTimeout(timeout)
      }
    }
  }
}
